import type { SyntheticEvent } from 'react'
import { getPlaceholderImageUrl } from '@utils/image'
import { ImageSize } from '@/constants'
import { resolveProductImage } from '../utils/omp'
import { useDecodedImage } from '../hooks/useDecodedImage'

interface OmpCrossSellImageProps {
  // The offer's campaign product; its image usually lives on `product`.
  source: any
  size?: ImageSize
  alt?: string
}

// Resized file, then the full-size original, then the placeholder. The shared retry only looks at
// the campaign product's own image, which related products rarely have, and resized files can be
// missing on an environment.
function handleError(e: SyntheticEvent<HTMLImageElement>, source: any) {
  const target = e.currentTarget
  if (!target.dataset.fallback) {
    target.dataset.fallback = 'original'
    target.src = resolveProductImage(source, false, ImageSize.Original)
  } else if (target.dataset.fallback !== 'placeholder') {
    target.dataset.fallback = 'placeholder'
    target.src = getPlaceholderImageUrl()
  }
}

// Cross-sell's thumbnail. A shimmer holds the frame until the image is decoded, like
// OmpProductImage (see useDecodedImage).
const OmpCrossSellImage = ({ source, size = ImageSize.Medium, alt }: OmpCrossSellImageProps) => {
  const src = resolveProductImage(source, false, size)
  const ready = useDecodedImage(src)

  if (!ready) {
    return <span className="skeleton omp-product-image-loading" aria-hidden="true" />
  }

  return <img key={src} src={src} alt={alt ?? ''} decoding="sync" onError={e => handleError(e, source)} />
}

export default OmpCrossSellImage
